import { FC, useEffect, useRef } from "react";
import actions from "store/actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { salsaSelector, userMetadataSelector } from "store/selectors";

import { getSalsaAuthToken } from "services/salsa";
import { UserRole } from "types/BETypes";
import { SALSA_AUTH_TOKEN_REQUEST_DATETIME } from "constants/salsa";

interface Props {
  refreshIntervalTime?: number;
  refreshDelta?: number;
  checkInterval?: number;
}

const SalsaTokenRefresher: FC<Props> = ({
  refreshIntervalTime = Number(process.env.REACT_APP_SALSA_TOKEN_ACTIVITY_MINUTES || 60) *
    60 *
    1000,
  refreshDelta = 5 * 60 * 1000, //NOTE:::For requesting new token a bit earlier than expiration time comes;
  checkInterval = 10 * 1000,
}) => {
  const currentUser = useAppSelector(userMetadataSelector);
  const salsaData = useAppSelector(salsaSelector);
  const dispatch = useAppDispatch();
  const refreshInterval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const checkIfShouldRefreshToken = async () => {
    if (!currentUser?.activeCompanyId) return;

    const lastRequestTime = parseInt(
      localStorage.getItem(SALSA_AUTH_TOKEN_REQUEST_DATETIME) || "0",
      10,
    );
    const refreshTime = lastRequestTime + refreshIntervalTime - refreshDelta;
    const currentDate = Date.now();

    if (!!refreshTime && refreshTime < currentDate) {
      //NOTE:::If token is expired
      const _tokenRes = await getSalsaAuthToken(
        (currentUser?.activeCompanyUser?.companyRoles as UserRole[]) || [],
        currentUser?.activeCompanyUser?.lastActiveRole as UserRole,
        true,
      );
      if (!_tokenRes) return;

      dispatch(
        actions.salsaData.setSalsa({
          salsa: salsaData?.salsa,
          authToken: _tokenRes,
        }),
      );

      resetInterval();
      return true;
    }
    return false;
  };

  const resetInterval = () => {
    refreshInterval.current && window.clearInterval(refreshInterval.current);
    refreshInterval.current = setInterval(checkIfShouldRefreshToken, checkInterval);
  };

  const cleanUp = () => {
    refreshInterval && window.clearInterval(refreshInterval.current);
  };

  useEffect(() => {
    checkIfShouldRefreshToken();
  }, [currentUser?.activeCompanyUser?.lastActiveRole]);

  useEffect(() => {
    resetInterval();

    return () => {
      cleanUp();
    };
  }, []);

  return <></>;
};

export default SalsaTokenRefresher;
